import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle,
} from "@/components/ui/drawer";

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" || window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
};

const DEFAULT_CONTENT = "max-h-[85vh] overflow-y-auto border-white/10 bg-zinc-900 text-zinc-100";

/**
 * Form dialog responsif: Dialog terpusat di desktop (≥768px),
 * bottom sheet yang bisa di-swipe di mobile. Semua dialog berisi form
 * sebaiknya lewat komponen ini supaya polanya konsisten.
 */
const LIGHT_CONTENT = "max-h-[85vh] overflow-y-auto border border-slate-200 bg-white text-slate-900";

export default function ResponsiveFormDialog({
  open, onOpenChange, title, description, contentClassName, tone = "dark", children,
}) {
  const resolvedContent = contentClassName ?? (tone === "light" ? LIGHT_CONTENT : DEFAULT_CONTENT);
  const isDesktop = useIsDesktop();
  // Primitif dipakai tanpa Trigger sehingga Radix/Vaul tidak mengembalikan
  // fokus saat tutup: tangkap pemicu lewat efek (onOpenChange terkontrol
  // tidak menyala saat dibuka programatik), kembalikan saat tutup apa pun
  // penyebabnya (Escape, Batal, overlay, sukses simpan).
  const openerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      const target = openerRef.current;
      openerRef.current = null;
      setTimeout(() => {
        if (target && document.contains(target)) target.focus({ preventScroll: true });
      }, 0);
    };
  }, [open ]);

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={resolvedContent}>
          <DialogHeader>
            <DialogTitle className="font-heading">{title}</DialogTitle>
            {description && <DialogDescription className={tone === "light" ? "text-slate-500" : "text-zinc-500"}>{description}</DialogDescription>}
          </DialogHeader>
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent className={tone === "light" ? "max-h-[92vh] border border-slate-200 bg-white text-slate-900" : "max-h-[92vh] border-white/10 bg-zinc-900 text-zinc-100"}>
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-heading">{title}</DrawerTitle>
          {description && <DrawerDescription className={tone === "light" ? "text-slate-500" : "text-zinc-500"}>{description}</DrawerDescription>}
        </DrawerHeader>
        <div
          className="overflow-y-auto px-4 text-left"
          style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
          {children}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
